import { useState } from "react";
import api, { fmtIDR, apiErr } from "@/lib/api";
import { fmtDate } from "@/components/DateInput";
import { MasterImportModal } from "@/components/MasterImportModal";
import { useTableTools, TableToolbar, SelectAllBox, SelectBox } from "@/components/TableTools";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { Pencil, Trash2, Upload, Merge } from "lucide-react";
import { EmployeeForm } from "@/components/payroll/EmployeeForm";

const isActive = (e) => !e.resign_date && e.is_active !== false;
const laborLbl = (e) => (e.labor_type === "direct" ? "Langsung (HPP)" : "Tidak Langsung");

function EmpRow({ e, clientId, tools, reload }) {
  const del = async () => {
    if (!window.confirm(`Hapus ${e.name}? Riwayat transaksi PPh 21 tetap disimpan.`)) return;
    try { await api.delete(`/employees/${e.id}`); toast.success("Karyawan dihapus"); reload(); } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
  };
  return (
    <TableRow data-testid={`emp-row-${e.id}`} className={tools.sel.includes(e.id) ? "bg-primary/5" : ""}>
      <TableCell><SelectBox tools={tools} row={e} id="emp" /></TableCell>
      <TableCell className="text-xs font-mono">{e.employee_code || "—"}</TableCell>
      <TableCell className="text-sm font-medium">{e.name}<span className="block text-[10px] text-muted-foreground">{e.position}</span></TableCell>
      <TableCell className="text-xs" data-testid={`emp-labor-${e.id}`}>{laborLbl(e)}</TableCell>
      <TableCell className="text-xs num">{e.nik || "—"}</TableCell>
      <TableCell className="text-xs num">{e.npwp || "—"}</TableCell>
      <TableCell className="text-xs">{e.ptkp_status}</TableCell>
      <TableCell className="text-xs num">{fmtDate(e.join_date) || "—"}</TableCell>
      <TableCell className="text-xs num">{fmtDate(e.resign_date) || "—"}</TableCell>
      <TableCell className="text-xs">{e.employment_status === "tidak_tetap" ? "Tidak Tetap" : "Tetap"}</TableCell>
      <TableCell className="text-xs">{e.tax_method === "grossup" ? "Gross-Up" : "Gross"}</TableCell>
      <TableCell><Badge variant={isActive(e) ? "default" : "outline"} className="text-[10px]" data-testid={`emp-status-${e.id}`}>{isActive(e) ? "Aktif" : "Tidak Aktif"}</Badge></TableCell>
      <TableCell className="num text-right text-xs">{Number(e.loan_balance || 0) > 0 ? fmtIDR(e.loan_balance) : "—"}</TableCell>
      <TableCell className="text-right whitespace-nowrap">
        <EmployeeForm clientId={clientId} editEmp={e} onDone={reload} trigger={<Button variant="ghost" size="icon" className="h-7 w-7" data-testid={`emp-edit-${e.id}`}><Pencil className="h-4 w-4" /></Button>} />
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={del} data-testid={`emp-del-${e.id}`}><Trash2 className="h-4 w-4" /></Button>
      </TableCell>
    </TableRow>
  );
}

export function EmployeesTab({ clientId, employees, reload }) {
  const [importOpen, setImportOpen] = useState(false);
  const tools = useTableTools(employees, { date: (e) => e.join_date, text: (e) => `${e.name} ${e.nik} ${e.npwp} ${e.employee_code} ${e.position} ${laborLbl(e)}` });
  const bulkDel = async (ids) => {
    const { data } = await api.post("/employees/bulk-delete", { client_id: clientId, ids });
    reload();
    return { failed: ids.length - (data.deleted || 0) };
  };
  const cols = [["ID", "employee_code"], ["Nama", "name"], ["Jabatan", "position"], ["Tenaga Kerja", laborLbl], ["NIK", "nik"], ["NPWP", "npwp"],
    ["PTKP", "ptkp_status"], ["Tgl Masuk", "join_date"], ["Tgl Resign", "resign_date"], ["Status Pegawai", (e) => (e.employment_status === "tidak_tetap" ? "Tidak Tetap" : "Tetap")],
    ["Metode", (e) => (e.tax_method === "grossup" ? "Gross-Up" : "Gross")], ["Jenis Kelamin", "gender"], ["Sisa Hutang", "loan_balance"]];
  const merge = async () => {
    if (!window.confirm("Gabungkan karyawan dengan ID / Nama + NIK yang sama menjadi satu? Riwayat transaksi ikut digabung.")) return;
    try { const { data } = await api.post(`/employees/merge-duplicates?client_id=${clientId}`); toast.success(`${data.merged} data duplikat digabung`); reload(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };
  return (
    <Card className="p-4 space-y-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="font-head font-semibold">Master Karyawan ({tools.view.length})</h2>
        <TableToolbar tools={tools} id="emp" cols={cols} filename="master_karyawan" onDelete={bulkDel}>
          <Button variant="outline" size="sm" className="h-9" onClick={merge} data-testid="emp-merge-dup"><Merge className="h-4 w-4 mr-1" />Gabungkan Duplikat</Button>
          <Button variant="outline" size="sm" className="h-9" onClick={() => setImportOpen(true)} data-testid="emp-import"><Upload className="h-4 w-4 mr-1" />Import</Button>
          <EmployeeForm clientId={clientId} onDone={reload} />
        </TableToolbar>
      </div>
      <div className="overflow-auto rounded-md border border-border">
        <Table>
          <TableHeader><TableRow className="bg-secondary/50">
            <TableHead className="w-10"><SelectAllBox tools={tools} id="emp" /></TableHead>
            <TableHead>ID</TableHead><TableHead>Nama</TableHead><TableHead>Tenaga Kerja</TableHead><TableHead>NIK</TableHead><TableHead>NPWP</TableHead><TableHead>PTKP</TableHead>
            <TableHead>Tgl Masuk</TableHead><TableHead>Tgl Resign</TableHead><TableHead>Status Pegawai</TableHead><TableHead>Metode</TableHead>
            <TableHead>Status</TableHead><TableHead className="text-right">Sisa Hutang</TableHead><TableHead></TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {tools.view.length === 0 && <TableRow><TableCell colSpan={14} className="text-center py-8 text-muted-foreground">Belum ada karyawan</TableCell></TableRow>}
            {tools.view.map((e) => <EmpRow key={e.id} e={e} clientId={clientId} tools={tools} reload={reload} />)}
          </TableBody>
        </Table>
      </div>
      <MasterImportModal open={importOpen} onOpenChange={setImportOpen} clientId={clientId} type="employees" label="Karyawan" onImported={reload} />
    </Card>
  );
}
